/**
 * Income-tax Act versioning.
 *
 * The Income-tax Act, 2025 came into force on 1 April 2026 and repealed the
 * 1961 Act. Almost every section is renumbered, the entire 194-series of TDS
 * provisions is consolidated into three parent sections, and returns and
 * challans now carry numeric payment codes instead of section numbers.
 *
 * The software cannot simply switch over, because section 536 of the 2025 Act
 * preserves the old law for periods before the cutover. A firm closing FY
 * 2025-26 in September 2026 is filing under the 1961 Act while simultaneously
 * deducting TDS under the 2025 Act on current payments. Both must be right at
 * the same time.
 *
 * So the applicable law is a function of the transaction date, never a global
 * setting. Every citation the product shows is resolved through this module.
 *
 * Applicability test: the Income Tax Department has confirmed it follows the
 * *earlier of credit or payment*. If that earlier event falls on or before
 * 31 March 2026, the 1961 Act applies; on or after 1 April 2026, the 2025 Act.
 */

export type ActVersion = "1961" | "2025";

/** The 2025 Act applies to events on or after this date. */
export const ACT_2025_EFFECTIVE_FROM = "2026-04-01";

/**
 * Determines the governing Act from the earlier of credit or payment.
 * Passing both dates is preferred; passing one is treated as that event.
 */
export const actVersionFor = (creditDate: string, paymentDate?: string): ActVersion => {
  const trigger = paymentDate && paymentDate < creditDate ? paymentDate : creditDate;
  return trigger >= ACT_2025_EFFECTIVE_FROM ? "2025" : "1961";
};

/** Tax year label. The 2025 Act drops "assessment year" entirely. */
export const taxYearFor = (date: string): string => {
  const parsed = new Date(`${date.slice(0, 10)}T00:00:00Z`);
  const start = parsed.getUTCMonth() >= 3 ? parsed.getUTCFullYear() : parsed.getUTCFullYear() - 1;
  return `${start}-${String((start + 1) % 100).padStart(2, "0")}`;
};

/**
 * How confident we are in a mapping. Some payment codes were inferred from
 * draft forms and are not yet notified by the CBDT, and it would be dishonest
 * to present those with the same certainty as a mapping stated in the Act.
 */
export type MappingConfidence = "confirmed" | "provisional";

export interface SectionMapping {
  /** Section under the 1961 Act. */
  old: string;
  /** Corresponding provision under the 2025 Act. */
  new: string;
  subject: string;
  confidence: MappingConfidence;
  note?: string;
}

/**
 * Section concordance for the provisions this product cites. Deliberately
 * limited to what the software actually uses — a partial table that is correct
 * is more useful than a complete one that is half-guessed.
 */
export const SECTION_MAP: SectionMapping[] = [
  { old: "192", new: "392", subject: "TDS on salary", confidence: "confirmed" },
  { old: "194A", new: "393", subject: "TDS on interest other than securities", confidence: "confirmed", note: "Now a table entry within section 393 rather than a standalone section." },
  { old: "194C", new: "393", subject: "TDS on payments to contractors", confidence: "confirmed", note: "Now a table entry within section 393." },
  { old: "194H", new: "393", subject: "TDS on commission or brokerage", confidence: "confirmed" },
  { old: "194I", new: "393", subject: "TDS on rent", confidence: "confirmed" },
  { old: "194IB", new: "393", subject: "TDS on rent by individuals and HUFs", confidence: "confirmed", note: "Rate reduced from 5% to 2% with effect from 1 April 2026." },
  { old: "194J", new: "393", subject: "TDS on professional or technical services", confidence: "confirmed" },
  { old: "194Q", new: "393", subject: "TDS on purchase of goods", confidence: "confirmed", note: "Rate of 0.1% and the 50 lakh threshold are unchanged." },
  { old: "195", new: "393", subject: "TDS on payments to non-residents", confidence: "provisional", note: "Sources differ on whether non-resident deduction sits in section 393 or a separate provision. Verify before relying on the citation." },
  { old: "206C", new: "394", subject: "Tax collected at source", confidence: "confirmed" },
  { old: "206C(1H)", new: "394", subject: "TCS on sale of goods", confidence: "confirmed" },
  { old: "206AA", new: "396", subject: "Higher rate where PAN is not furnished", confidence: "provisional", note: "The requirement survives; the exact section number is not confirmed here." },
  { old: "197", new: "395", subject: "Certificate for lower or nil deduction", confidence: "provisional", note: "The certificate mechanism survives; the exact section number is not confirmed here." },
  { old: "32", new: "33", subject: "Depreciation", confidence: "confirmed" },
  { old: "32(1)(iia)", new: "33", subject: "Additional depreciation on new plant and machinery", confidence: "confirmed", note: "Additional depreciation at 20% continues within the depreciation provision." },
  { old: "43B", new: "37", subject: "Deductions allowed only on actual payment", confidence: "confirmed" },
  { old: "43B(h)", new: "37(2)(g)", subject: "Disallowance of delayed payments to micro and small enterprises", confidence: "confirmed", note: "The MSMED Act section 15 time limit and the actual-payment rule are unchanged." },
  { old: "44AB", new: "63", subject: "Tax audit", confidence: "confirmed" },
  { old: "80C", new: "123", subject: "Deduction for specified investments", confidence: "confirmed", note: "The whole 80C to 80U range is renumbered to 122 to 154." },
  { old: "10", new: "Schedule II", subject: "Exemptions", confidence: "confirmed", note: "Exemptions have moved out of a section and into Schedule II." }
];

const BY_OLD = new Map(SECTION_MAP.map((entry) => [entry.old, entry]));
const BY_NEW = new Map<string, SectionMapping[]>();
for (const entry of SECTION_MAP) {
  const bucket = BY_NEW.get(entry.new) ?? [];
  bucket.push(entry);
  BY_NEW.set(entry.new, bucket);
}

export const mapOldToNew = (oldSection: string): SectionMapping | undefined => BY_OLD.get(oldSection);
export const mapNewToOld = (newSection: string): SectionMapping[] => BY_NEW.get(newSection) ?? [];

/**
 * Resolves the citation to show for a transaction. Returns both numbers so the
 * UI can show the new one with the familiar old one alongside during the
 * transition — practitioners think in 194C for years yet.
 */
export interface Citation {
  version: ActVersion;
  section: string;
  legacySection?: string;
  subject: string;
  display: string;
  confidence: MappingConfidence;
  note?: string;
}

export const cite = (oldSection: string, transactionDate: string, paymentDate?: string): Citation => {
  const version = actVersionFor(transactionDate, paymentDate);
  const mapping = BY_OLD.get(oldSection);

  if (version === "1961" || !mapping) {
    return {
      version,
      section: oldSection,
      subject: mapping?.subject ?? oldSection,
      display: `section ${oldSection} of the Income-tax Act, 1961`,
      confidence: mapping?.confidence ?? "confirmed",
      note: version === "1961" ? "Preserved by the section 536 saving clause of the 2025 Act." : mapping?.note
    };
  }

  return {
    version,
    section: mapping.new,
    legacySection: mapping.old,
    subject: mapping.subject,
    display: `section ${mapping.new} of the Income-tax Act, 2025 (formerly section ${mapping.old})`,
    confidence: mapping.confidence,
    note: mapping.note
  };
};

// -------------------------------------------------------- Payment codes

/**
 * From 1 April 2026 challans and returns carry a numeric payment code instead
 * of a section number. Filing with an old section code makes the return
 * defective, so this mapping is load-bearing rather than cosmetic.
 *
 * Only codes this product needs are listed. Several codes across the full
 * 1001–1092 range were inferred from draft forms and are not yet notified;
 * none of those are relied on here.
 */
export type DeducteeCategory = "individual-huf" | "other" | "any";

export interface PaymentCode {
  code: string;
  legacySection: string;
  description: string;
  parentSection: string;
  /**
   * Several legacy sections split into more than one code by deductee type —
   * 194C is the obvious case. Looking up on section alone silently picks the
   * wrong code and produces a defective return, so the category is part of the
   * key rather than an afterthought.
   */
  deducteeCategory: DeducteeCategory;
  confidence: MappingConfidence;
}

export const PAYMENT_CODES: PaymentCode[] = [
  { code: "1001", legacySection: "192", description: "Salary", parentSection: "392", deducteeCategory: "any", confidence: "provisional" },
  { code: "1005", legacySection: "194C", description: "Payment to resident contractor — individual or HUF", parentSection: "393", deducteeCategory: "individual-huf", confidence: "provisional" },
  { code: "1006", legacySection: "194C", description: "Payment to resident contractor — other than individual or HUF", parentSection: "393", deducteeCategory: "other", confidence: "provisional" },
  { code: "1003", legacySection: "194A", description: "Interest other than on securities", parentSection: "393", deducteeCategory: "any", confidence: "provisional" },
  { code: "1008", legacySection: "194H", description: "Commission or brokerage", parentSection: "393", deducteeCategory: "any", confidence: "provisional" },
  { code: "1009", legacySection: "194I", description: "Rent", parentSection: "393", deducteeCategory: "any", confidence: "provisional" },
  { code: "1011", legacySection: "194J", description: "Professional or technical services", parentSection: "393", deducteeCategory: "any", confidence: "provisional" },
  { code: "1021", legacySection: "194Q", description: "Purchase of goods", parentSection: "393", deducteeCategory: "any", confidence: "provisional" }
];

/**
 * The payment code for a legacy section, where the transaction falls under the
 * 2025 Act. Returns undefined under the 1961 Act, where no code is used.
 *
 * Every code here is marked provisional. The CBDT had not notified the full
 * code list at the time this mapping was compiled, so the UI must surface the
 * caveat rather than let a user file on an unverified code.
 */
export const paymentCodeFor = (
  legacySection: string,
  transactionDate: string,
  paymentDate?: string,
  deducteeCategory: DeducteeCategory = "other"
): PaymentCode | undefined => {
  if (actVersionFor(transactionDate, paymentDate) === "1961") return undefined;
  const candidates = PAYMENT_CODES.filter((entry) => entry.legacySection === legacySection);
  if (candidates.length === 0) return undefined;
  if (candidates.length === 1) return candidates[0];
  // Where a section splits by deductee type, match the category exactly and
  // fall back only to a code that genuinely applies to any deductee.
  return (
    candidates.find((entry) => entry.deducteeCategory === deducteeCategory) ??
    candidates.find((entry) => entry.deducteeCategory === "any")
  );
};

/** Every code defined for a legacy section — used by the UI to show the choice. */
export const paymentCodesForSection = (legacySection: string): PaymentCode[] =>
  PAYMENT_CODES.filter((entry) => entry.legacySection === legacySection);

// --------------------------------------------------------------- Forms

export interface FormMapping {
  legacyForm: string;
  newForm: string;
  subject: string;
  confidence: MappingConfidence;
  note?: string;
}

/**
 * Return and certificate forms were renamed alongside the Act.
 *
 * Published sources disagree on several of these numbers, so all are marked
 * provisional and the product must not present them as settled. Filing on the
 * wrong form number is a rejection, and a confident wrong answer is worse than
 * a flagged uncertain one.
 */
export const FORM_MAP: FormMapping[] = [
  { legacyForm: "24Q", newForm: "138", subject: "Quarterly TDS statement — salary", confidence: "provisional", note: "Sources differ on the exact number. Confirm against the CBDT notification before filing." },
  { legacyForm: "26Q", newForm: "140", subject: "Quarterly TDS statement — other than salary", confidence: "provisional", note: "One source cites Form 139 for this return. Confirm before filing." },
  { legacyForm: "27Q", newForm: "144", subject: "Quarterly TDS statement — non-resident payments", confidence: "provisional", note: "Sources conflict between Form 140 and Form 144." },
  { legacyForm: "27EQ", newForm: "143", subject: "Quarterly TCS statement", confidence: "provisional" },
  { legacyForm: "16", newForm: "130", subject: "TDS certificate — salary", confidence: "provisional" },
  { legacyForm: "16A", newForm: "131", subject: "TDS certificate — other than salary", confidence: "provisional" },
  { legacyForm: "15G/15H", newForm: "121", subject: "Declaration for no deduction", confidence: "provisional", note: "Forms 15G and 15H are merged into a single form." }
];

const FORM_BY_LEGACY = new Map(FORM_MAP.map((entry) => [entry.legacyForm, entry]));

export const formFor = (legacyForm: string, periodEndDate: string): FormMapping | { legacyForm: string; newForm: string; subject: string; confidence: MappingConfidence } => {
  if (actVersionFor(periodEndDate) === "1961") {
    const mapping = FORM_BY_LEGACY.get(legacyForm);
    return { legacyForm, newForm: legacyForm, subject: mapping?.subject ?? legacyForm, confidence: "confirmed" };
  }
  return FORM_BY_LEGACY.get(legacyForm) ?? { legacyForm, newForm: legacyForm, subject: legacyForm, confidence: "provisional" };
};

// ------------------------------------------------------------ Readiness

export interface TransitionIssue {
  severity: "error" | "warning" | "info";
  code: string;
  message: string;
}

/**
 * Checks a transaction for cutover problems. The dangerous case is a document
 * dated on or after 1 April 2026 still carrying an old section code — the
 * return will be rejected as defective, and the deductor only finds out at
 * filing time.
 */
export const checkTransition = (input: {
  transactionDate: string;
  paymentDate?: string;
  citedSection?: string;
  citedPaymentCode?: string;
  citedForm?: string;
}): TransitionIssue[] => {
  const issues: TransitionIssue[] = [];
  const version = actVersionFor(input.transactionDate, input.paymentDate);

  if (version === "2025" && input.citedSection && BY_OLD.has(input.citedSection)) {
    const mapping = BY_OLD.get(input.citedSection)!;
    issues.push({
      severity: "error",
      code: "LEGACY_SECTION_AFTER_CUTOVER",
      message: `This transaction falls under the Income-tax Act, 2025 but cites section ${input.citedSection} of the 1961 Act. Use section ${mapping.new}. Returns filed with the old code are treated as defective.`
    });
  }

  if (version === "2025" && !input.citedPaymentCode && input.citedSection) {
    const candidates = paymentCodesForSection(input.citedSection);
    const code = candidates.length === 1 ? candidates[0] : undefined;
    issues.push({
      severity: "warning",
      code: "PAYMENT_CODE_MISSING",
      message: code
        ? `Challans and returns from 1 April 2026 carry a numeric payment code. Code ${code.code} corresponds to the former section ${input.citedSection}.`
        : candidates.length > 1
          ? `Challans and returns from 1 April 2026 carry a numeric payment code. Former section ${input.citedSection} splits into ${candidates.map((entry) => entry.code).join(" and ")} by deductee type, so the deductee must be classified before filing.`
          : "Challans and returns from 1 April 2026 carry a numeric payment code, which has not been set on this transaction."
    });
  }

  if (version === "1961" && input.citedPaymentCode) {
    issues.push({
      severity: "error",
      code: "NEW_CODE_BEFORE_CUTOVER",
      message: "This transaction is governed by the 1961 Act, where numeric payment codes do not apply. Cite the section instead."
    });
  }

  const code = input.citedPaymentCode ? PAYMENT_CODES.find((entry) => entry.code === input.citedPaymentCode) : undefined;
  if (code?.confidence === "provisional") {
    issues.push({
      severity: "warning",
      code: "PROVISIONAL_CODE",
      message: `Payment code ${code.code} was compiled before the CBDT notified the full code list. Verify it against the current notification before filing.`
    });
  }

  if (input.citedForm) {
    const form = FORM_BY_LEGACY.get(input.citedForm);
    if (version === "2025" && form) {
      issues.push({
        severity: form.confidence === "provisional" ? "warning" : "info",
        code: "FORM_RENAMED",
        message: `Form ${input.citedForm} is renamed to Form ${form.newForm} from Tax Year 2026-27.${form.note ? ` ${form.note}` : ""}`
      });
    }
  }

  return issues;
};

/** A one-line summary of which law governs, for display on a document. */
export const governingLawLabel = (transactionDate: string, paymentDate?: string): string => {
  const version = actVersionFor(transactionDate, paymentDate);
  return version === "2025"
    ? `Income-tax Act, 2025 · Tax Year ${taxYearFor(transactionDate)}`
    : `Income-tax Act, 1961 · FY ${taxYearFor(transactionDate)} (preserved by section 536)`;
};
